import { useState, useEffect, useCallback } from "react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import {
  RefreshCw,
  Loader2,
  Zap,
  CheckCircle,
  XCircle,
  Shield,
  Users,
  Wallet,
  Webhook,
  BarChart3,
  Copy,
  PlugZap,
  ClipboardCheck,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const StatusDot = ({ ok }) =>
  ok ? (
    <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
  ) : (
    <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
  );

const ReverseSyncPanel = ({ getAdminHeaders }) => {
  const [status, setStatus] = useState(null);
  const [stats, setStats] = useState(null);
  const [verifyResult, setVerifyResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [loading, setLoading] = useState(true);

  const headers = useCallback(
    () => ({ ...getAdminHeaders(), "Content-Type": "application/json" }),
    [getAdminHeaders]
  );

  const fetchAll = useCallback(async () => {
    try {
      const [rs, st] = await Promise.all([
        fetch(`${API}/admin/connect/reverse-status`, { credentials: "include", headers: headers() }),
        fetch(`${API}/admin/connect/sync-stats`, { credentials: "include", headers: headers() }),
      ]);
      if (rs.ok) setStatus(await rs.json());
      if (st.ok) setStats(await st.json());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const runReverseConfig = async () => {
    setRunning(true);
    try {
      const res = await fetch(`${API}/admin/connect/reverse-config`, {
        method: "POST",
        credentials: "include",
        headers: headers(),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(
          `Configuración en reversa completada: ${data.endpoints_linked?.length || 0} endpoints, ` +
            `${data.users?.created || 0} usuarios creados, ${data.users?.updated || 0} actualizados`
        );
        fetchAll();
      } else {
        toast.error(data.detail || "Error en la configuración en reversa");
      }
    } catch (e) {
      toast.error("Error de conexión");
    } finally {
      setRunning(false);
    }
  };

  const runVerification = async () => {
    setVerifying(true);
    try {
      const res = await fetch(`${API}/admin/connect/verify`, {
        method: "POST",
        credentials: "include",
        headers: headers(),
      });
      const data = await res.json();
      if (res.ok) {
        setVerifyResult(data);
        if (data.verdict === "operational") toast.success("Verificación: 100% funcional y operativo");
        else toast.warning(`Verificación: ${data.passed}/${data.total} checks OK`);
        fetchAll();
      } else {
        toast.error(data.detail || "Error en la verificación");
      }
    } catch (e) {
      toast.error("Error de conexión");
    } finally {
      setVerifying(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiado al portapapeles");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-red-500" />
      </div>
    );
  }

  const conn = status?.connection;
  const counts = status?.counts || {};

  return (
    <div className="space-y-6">
      {/* Reverse config action */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <CardTitle className="text-white flex items-center gap-2">
                <PlugZap className="w-5 h-5 text-red-400" /> Configuración en Reversa
              </CardTitle>
              <CardDescription>
                Detecta la plataforma conectada, vincula todos los endpoints (GET/POST), configura OAuth SSO,
                sincroniza usuarios y OkWallet, y crea los webhooks necesarios.
              </CardDescription>
            </div>
            <div className="flex gap-3">
              <Button onClick={fetchAll} variant="outline" size="sm" className="border-slate-600 text-slate-300">
                <RefreshCw className="w-4 h-4 mr-2" /> Actualizar
              </Button>
              <Button
                onClick={runReverseConfig}
                disabled={running}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="run-reverse-config-btn"
              >
                {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
                {running ? "Ejecutando..." : "Ejecutar Configuración en Reversa"}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-slate-900/60 rounded-xl p-4 border border-slate-700/60">
              <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
                <PlugZap className="w-4 h-4" /> Plataforma
              </div>
              <div className="text-white font-semibold">
                {conn?.platform_info?.name || "No detectada"}
              </div>
              <Badge className={conn?.status === "connected" ? "bg-emerald-600 mt-2" : "bg-slate-600 mt-2"}>
                {conn?.status === "connected" ? "Conectada" : "Pendiente"}
              </Badge>
            </div>
            <div className="bg-slate-900/60 rounded-xl p-4 border border-slate-700/60">
              <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
                <Zap className="w-4 h-4" /> Endpoints vinculados
              </div>
              <div className="text-2xl font-bold text-white">{conn?.selected_endpoints?.length || 0}</div>
            </div>
            <div className="bg-slate-900/60 rounded-xl p-4 border border-slate-700/60">
              <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
                <Users className="w-4 h-4" /> Usuarios vinculados
              </div>
              <div className="text-2xl font-bold text-white">{counts.users_linked ?? 0}</div>
            </div>
            <div className="bg-slate-900/60 rounded-xl p-4 border border-slate-700/60">
              <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
                <Shield className="w-4 h-4" /> OAuth SSO
              </div>
              <Badge className={status?.oauth_sso?.status === "configured" ? "bg-emerald-600" : "bg-slate-600"}>
                {status?.oauth_sso?.status === "configured" ? "Configurado" : "Pendiente"}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* OkWallet */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Wallet className="w-5 h-5 text-amber-400" /> OkWallet Sincronizado
          </CardTitle>
          <CardDescription>Saldos, retiros y recargas unificados desde ambas plataformas</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            {[
              ["Saldos", counts.balances],
              ["Retiros", counts.withdrawals],
              ["Recargas", counts.recharges],
            ].map(([label, value]) => (
              <div key={label} className="bg-slate-900/60 rounded-xl p-4 border border-slate-700/60 text-center">
                <div className="text-2xl font-bold text-white">{value ?? 0}</div>
                <div className="text-slate-400 text-sm">{label}</div>
              </div>
            ))}
          </div>
          {status?.last_run?.okwallet && (
            <div className="mt-4 space-y-1 text-xs text-slate-400">
              {Object.entries(status.last_run.okwallet).map(([k, v]) => (
                <div key={k} className="flex items-center gap-2">
                  <StatusDot ok={v.status === "synced"} />
                  <span className="capitalize">{k}:</span>
                  <span>{v.status === "not_available" ? v.detail || "No disponible en la Data API" : `${v.synced} registros (${v.status})`}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Endpoint stats */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-400" /> Estadísticas por Endpoint
          </CardTitle>
          <CardDescription>
            Totales: {stats?.totals?.calls || 0} llamadas · {stats?.totals?.success || 0} exitosas ·{" "}
            {stats?.totals?.errors || 0} errores
          </CardDescription>
        </CardHeader>
        <CardContent>
          {(!stats?.endpoints || stats.endpoints.length === 0) ? (
            <p className="text-slate-500 text-sm">Aún no hay estadísticas. Ejecuta la configuración en reversa.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-slate-400 text-left border-b border-slate-700">
                    <th className="py-2 pr-4">Endpoint</th>
                    <th className="py-2 pr-4">Método</th>
                    <th className="py-2 pr-4">Dirección</th>
                    <th className="py-2 pr-4">Llamadas</th>
                    <th className="py-2 pr-4">Éxitos</th>
                    <th className="py-2 pr-4">Errores</th>
                    <th className="py-2">Última</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.endpoints.map((s, i) => (
                    <tr key={i} className="border-b border-slate-800 text-slate-300">
                      <td className="py-2 pr-4 font-mono text-xs">{s.endpoint}</td>
                      <td className="py-2 pr-4">
                        <Badge className={s.method === "GET" ? "bg-blue-600" : "bg-purple-600"}>{s.method}</Badge>
                      </td>
                      <td className="py-2 pr-4 text-xs">{s.direction === "inbound" ? "Entrante" : "Saliente"}</td>
                      <td className="py-2 pr-4">{s.calls || 0}</td>
                      <td className="py-2 pr-4 text-emerald-400">{s.success || 0}</td>
                      <td className="py-2 pr-4 text-red-400">{s.errors || 0}</td>
                      <td className="py-2 text-xs">{s.last_call ? new Date(s.last_call).toLocaleString() : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Webhooks */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Webhook className="w-5 h-5 text-purple-400" /> Webhooks
          </CardTitle>
          <CardDescription>
            Configura estos webhooks en la plataforma remota con el header <code>X-OkGo-Secret</code> (pestaña OkGo)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {(!status?.webhooks || status.webhooks.length === 0) ? (
            <p className="text-slate-500 text-sm">Los webhooks se crean al ejecutar la configuración en reversa.</p>
          ) : (
            <div className="space-y-2">
              {status.webhooks.map((w, i) => (
                <div key={i} className="flex items-center gap-3 bg-slate-900/60 rounded-lg p-3 border border-slate-700/60">
                  <Badge className="bg-purple-600">{w.method}</Badge>
                  <span className="text-slate-300 text-xs">{w.event}</span>
                  <code className="text-xs text-slate-400 flex-1 truncate">{w.url}</code>
                  <button onClick={() => copy(w.url)} className="text-slate-500 hover:text-white">
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Verification */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <CardTitle className="text-white flex items-center gap-2">
                <ClipboardCheck className="w-5 h-5 text-emerald-400" /> Verificación Final
              </CardTitle>
              <CardDescription>Comprueba que todo el módulo sea 100% funcional y operativo</CardDescription>
            </div>
            <Button
              onClick={runVerification}
              disabled={verifying}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="run-verification-btn"
            >
              {verifying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              {verifying ? "Verificando..." : "Ejecutar Verificación"}
            </Button>
          </div>
        </CardHeader>
        {verifyResult && (
          <CardContent>
            <div className="mb-4">
              <Badge
                className={
                  verifyResult.verdict === "operational"
                    ? "bg-emerald-600"
                    : verifyResult.verdict === "partial"
                    ? "bg-amber-600"
                    : "bg-red-600"
                }
              >
                {verifyResult.verdict === "operational"
                  ? "100% Operativo"
                  : verifyResult.verdict === "partial"
                  ? `Parcial (${verifyResult.passed}/${verifyResult.total})`
                  : "Fallido"}
              </Badge>
            </div>
            <div className="space-y-2">
              {verifyResult.checks.map((c, i) => (
                <div key={i} className="flex items-start gap-3 bg-slate-900/60 rounded-lg p-3 border border-slate-700/60">
                  <StatusDot ok={c.status === "pass"} />
                  <div>
                    <div className="text-slate-200 text-sm font-medium">{c.check}</div>
                    <div className="text-slate-400 text-xs">{c.detail}</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default ReverseSyncPanel;
